// ─────────────────────────────────────────────
//  caseRole — may THIS account stamp a phase, acknowledge one, or neither?
//
//  Pure. The Room Status screen renders one shared PhaseRow in two modes, and
//  this is the only thing that decides which mode a viewer gets.
//
//  ⚠️ THE ANSWER COMES FROM THE ROOM'S LIVE ASSIGNMENT, NEVER FROM THE CASE
//  DOCUMENT. `or_cases.providerId` records who OPENED the case; it is not a
//  permission check. CRNAs are relieved for lunch every single day, and
//  domain/relief's takeoverPatch works by REPLACING `assignment.ahpId` with
//  `crnaReliefId` (relief.js:45) — so after a takeover the live row already
//  names the person actually standing in the room. Key on the case doc instead
//  and the reliever taps Closing and is silently denied: the card says WOR4,
//  nothing happens, and there is no error a clinician can act on. That was
//  caught in clinical review on 2026-09-07 before it shipped.
//
//  ⚠️ A STAGED RELIEF IS NOT A TAKEOVER. `crnaReliefId` is set while the relief
//  is only planned; `ahpId` changes when it is confirmed. Reading `ahpId` is
//  therefore correct in both states — the person who has actually taken the
//  room is the person who may stamp it.
//
//  ⚠️ FAILS CLOSED. An unresolved id is not a match. A null foreign key means
//  "we do not know", never "nobody", so an account we cannot place in this room
//  gets `null` — read-only — rather than the benefit of the doubt.
// ─────────────────────────────────────────────

// The person standing in the room right now, and the anaesthesiologist
// directing it. The room doc is the fallback the board itself uses when a row
// has not been written yet (RoomCard resolves `asg.ahpName || room.providerName`
// the same way), so the two surfaces agree about who is in a room.
export function currentProviderId(assignment, room) {
  return assignment?.ahpId || room?.providerId || null;
}

export function currentAttendingId(assignment, room) {
  return assignment?.attendingId || room?.supervisingDoctorId || null;
}

// One anaesthesiologist running their own case: the same human is both the
// person stamping and the person who would acknowledge. The screen hides
// Acknowledge in this state rather than letting one person acknowledge
// themselves — see the spec's note that rules alone cannot express "not the
// same human".
export function isSoloCase(assignment, room) {
  const p = currentProviderId(assignment, room);
  const a = currentAttendingId(assignment, room);
  return !!p && p === a;
}

/**
 * "provider"   — may stamp phases and the two case boundaries.
 * "supervisor" — may acknowledge phases, and nothing else.
 * null         — read-only.
 *
 * Provider wins when one person is both, because a solo anaesthetist needs the
 * buttons. Deriving the mode from the account's ROLE instead would hand a solo
 * MD the acknowledge-only screen and leave them unable to stamp their own case.
 */
export function caseWriterMode({ uid, staffIds, assignment, room } = {}) {
  if (!uid) return null;

  // ⚠️ AN ASSIGNMENT NAMES PEOPLE BY STAFF ID, NOT BY AUTH UID, and comparing
  // the two directly is why every Room Status screen rendered read-only: no
  // CRNA could stamp their own phases and no supervisor could acknowledge one.
  // `assignment.ahpId` is a `staff/{id}` document id; `uid` is the Firebase
  // Auth id. They are different id spaces and they simply never matched.
  //
  // It hid well. `useMyRooms` answers the SAME question correctly, through
  // `myRooms.userStaffIds` (uid link, honorific-insensitive name, or short
  // code), so the room showed up as "mine" while the buttons did not — which
  // reads as a styling problem rather than an identity one. Two resolutions of
  // one question, and only the other one was right. Caught in the live Android
  // app on 2026-09-07.
  //
  // The uid stays in the set because some rows genuinely do store it, and
  // `userStaffIds` adds it for that reason too.
  // ⚠️ ALWAYS A NEW SET — never the caller's. BoardPage calls this once per room
  // inside its render map, holding `myStaffIds` as a memoized Set; adding to
  // that Set in place mutates a value other code has already read, with no
  // identity change to make React notice. A pure domain function must not write
  // to its argument.
  const ids = new Set(staffIds || []);
  ids.add(uid);

  // ⚠️ STILL FAILS CLOSED, and that is the half not to lose. A null foreign key
  // means "we do not know", never "nobody", so an unresolved id must not match
  // an empty set — a wrong stamp is a clinical record of a moment that did not
  // happen.
  const isMine = (id) => !!id && ids.has(id);

  if (isMine(currentProviderId(assignment, room))) return "provider";
  if (isMine(currentAttendingId(assignment, room))) return "supervisor";
  return null;
}
